// rraVerifyDraft — the PURE half of the verifier form (E2): what the verifier has
// filled in, the first problem with it, and the POST body. No network and no
// import.meta.env, so it runs under plain `tsx --test`. lib/rraFormClient.ts
// re-exports it alongside the wire types.
//
// The sentences are the server's own (routes/rraVerifyForm.ts), so a client
// check and a 400 read the same.

import type { RraProngAnswer, RraVerifyFormView, RraVerifySubmissionBody } from './rraFormClient';

/** What the verifier has filled in so far (the page's state, kept pure for tests). */
export interface RraVerifyFormDraft {
  positionToKnow: 'yes' | 'no' | undefined;
  disability: RraProngAnswer | undefined;
  need: RraProngAnswer | undefined;
  name: string;
  email: string;
}

export const BLANK_VERIFY_DRAFT: RraVerifyFormDraft = {
  positionToKnow: undefined,
  disability: undefined,
  need: undefined,
  name: '',
  email: '',
};

/** First problem with the draft as a plain sentence, or null when it can be sent.
 *  The sentences are the server's own, so a client check and a 400 read the same. */
export function verifyDraftProblem(
  view: Pick<RraVerifyFormView, 'askDisability' | 'askNeed'>,
  draft: RraVerifyFormDraft,
): string | null {
  if (!draft.name.trim()) return 'Please enter your name.';
  if (draft.positionToKnow === undefined) return 'Please answer the first question.'; // DRAFT
  if (draft.positionToKnow === 'yes') {
    if (view.askDisability && draft.disability === undefined) return 'Please answer each question.'; // DRAFT
    if (view.askNeed && draft.need === undefined) return 'Please answer each question.'; // DRAFT
  }
  return null;
}

/** The POST body: out-of-gap prongs are never sent, and a position-to-know "No"
 *  sends no prong answers at all (the form hides them). */
export function buildVerifySubmission(
  view: Pick<RraVerifyFormView, 'askDisability' | 'askNeed'>,
  draft: RraVerifyFormDraft,
): RraVerifySubmissionBody {
  if (draft.positionToKnow === undefined) {
    throw new Error('buildVerifySubmission: positionToKnow is required');
  }
  const name = draft.name.trim();
  const email = draft.email.trim();
  const yes = draft.positionToKnow === 'yes';
  return {
    positionToKnow: draft.positionToKnow,
    ...(yes && view.askDisability && draft.disability ? { disability: draft.disability } : {}),
    ...(yes && view.askNeed && draft.need ? { need: draft.need } : {}),
    respondent: { name, ...(email ? { email } : {}) },
  };
}

